import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  CacheExpiryConstants,
  CacheKeyConstants,
  CacheService,
  FareCalculationNotFoundException,
  ReservationStatusNameConstants,
  ServiceException,
  TimeZoneUtil,
  UnauthorizedException,
  isNullOrUndefined
} from '@uzurv/core';
import { AuthDataService, UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { mergeMap, Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, mergeMapTo } from 'rxjs/operators';
import {
  BillingSummary,
  CancellationReason,
  ChatMessage,
  DriverEarnings,
  DriverEta,
  FareAdjustment,
  FareReviewDetail,
  FareReviewItem,
  FareReviewStatusUpdate,
  InsurancePolicy,
  Itinerary,
  MiniDriver,
  RecoveryRide,
  Reservation,
  ReservationDetail,
  ReservationRouteSummary,
  ReservationStatus,
  ReservationStatusUpdate,
  RideRequestLocation,
  RiderFare,
  RiderProtectedReservationDetail,
  Trip,
  Vehicle
} from '../../models';
import { FileData } from '@uzurv/core';

@Injectable({
  providedIn: 'root'
})
export class ReservationService extends UzurvRestService {
  readonly resourceUrl = 'reservations/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService,
    private authDataService: AuthDataService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a list of eligible vehicles for the reservation
   *  @param reservationId
   */

  getEligibleVehiclesForReservationById(reservationId: number): Observable<Array<Vehicle>> {
    return this.get<Array<Vehicle>>(`${this.resourceUrl}${reservationId}/eligibleVehicles/`).pipe(
      map(response => Deserialize(response, Vehicle))
    );
  }

  /**
   * Returns an updated Reservation
   *  @param reservationId
   *  @param vehicleId
   */

  updateReservationVehicle(reservationId: number, vehicleId: number): Observable<Reservation> {
    return this.patch<Reservation>(`${this.resourceUrl}${reservationId}?vehicleId=${vehicleId}`, { vehicleId }).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Returns a list of reservations
   *
   * @param pageNumber
   * @param pageSize
   * @param cancelled
   * @permission ride_monitoring
   */
  getReservations(pageNumber: number, pageSize: number, cancelled: boolean = false): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      cancelled: cancelled
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * Returns a reservation by ID
   *
   * @param reservationId
   * @permission reservation_read
   */
  findReservationById(reservationId: number): Observable<ReservationDetail> {
    return this.get<ReservationDetail>(`${this.resourceUrl}${reservationId}`).pipe(
      map(response => Deserialize(response, ReservationDetail))
    );
  }

  /**
   * Returns a page of reservation results with the given status. The other params are optional
   *
   * @param pageNumber
   * @param pageSize
   * @param reservationStatusIds
   * @param transitProgramIds
   * @param vehicleTypeIds
   * @permission ride_monitoring
   */
  listReservationsByStatusAndVehicleTypeAndTransitProgram(
    pageNumber: number,
    pageSize: number,
    reservationStatusIds: number | Array<number>,
    transitProgramIds?: number | Array<number>,
    vehicleTypeIds?: number | Array<number>
  ): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      reservationStatusIds: reservationStatusIds,
      transitProgramIds: transitProgramIds,
      vehicleTypeIds: vehicleTypeIds
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * Returns a list of reservations by rider ID
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   * @param inactive
   */
  getReservationsByRiderId(
    riderId: number,
    pageNumber: number,
    pageSize: number,
    inactive: boolean = null
  ): Observable<Array<Reservation>> {
    const params = {
      pageNumber: pageNumber,
      pageSize: pageSize
    };

    if (!isNullOrUndefined(inactive)) {
      params['inactive'] = inactive;
    }

    return this.get<Array<Reservation>>(`riders/${riderId}/reservations/`, params).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * @param reservation
   * @param interestedDriverId
   * @permission ride_monitoring
   */
  createReservationWithProposedPickupTime(reservation: Reservation, interestedDriverId: number): Observable<Reservation> {
    return this.post<Reservation>(this.resourceUrl, Serialize(reservation, Reservation), {
      params: { interestedDriverId: interestedDriverId }
    }).pipe(map(response => Deserialize(response, Reservation)));
  }

  /**
   * @param reservation
   * @permission ride_monitoring
   */
  create(reservation: Reservation): Observable<Reservation> {
    return this.post<Reservation>(this.resourceUrl, Serialize(reservation, Reservation)).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Cancels a reservation with a cancellation reason
   *
   * @param reservationId
   * @param cancellationReason
   * @permission ride_cancel
   */
  cancelReservationWithCancellationReason(reservationId: number, cancellationReason: CancellationReason): Observable<Reservation> {
    const patchDictionary = {
      id: reservationId,
      cancelled: true,
      cancellationReason: cancellationReason
    };

    return this.patch<Reservation>(`${this.resourceUrl}${reservationId}`, patchDictionary).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Cancels a reservation and creates a recovery ride. Returns the created recovery itinerary if successful
   *
   * @param reservationId
   * @param cancellationReason
   * @permission ride_cancel
   */
  cancelReservationAndCreateRecoveryRide(
    reservationId: number,
    cancellationReason: CancellationReason,
    recoveryPickupLocation: RideRequestLocation,
    note: string
  ): Observable<Itinerary> {
    const recoveryRide: RecoveryRide = new RecoveryRide();

    recoveryRide.reservation = new Reservation();
    recoveryRide.reservation.id = reservationId;
    recoveryRide.reservation.cancelled = true;
    recoveryRide.reservation.cancellationReason = cancellationReason;

    recoveryRide.recoveryPickupLocation = recoveryPickupLocation;
    recoveryRide.note = note;

    return this.post<Itinerary>(`${this.resourceUrl}${reservationId}/createRecoveryRide/`, recoveryRide).pipe(
      map(response => Deserialize(response, Itinerary))
    );
  }

  /**
   * Cancels a reservation with a cancellation reason for a Web Self Service specifc user.
   *
   * @param reservationId
   * @param cancellationReason
   * @permission web_self_service_ride_cancel
   */
  cancelReservationWithCancellationReasonWebSelfServiceCancel(
    reservationId: number,
    cancellationReason: CancellationReason
  ): Observable<Reservation> {
    const patchDictionary = {
      id: reservationId,
      cancelled: true,
      cancellationReason: cancellationReason
    };

    return this.patch<Reservation>(`${this.resourceUrl}${reservationId}/webSelfServiceCancel/`, patchDictionary).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Returns a list of reservation statuses
   *
   * @param overrideCache
   * @permission reservation_read
   */
  getReservationStatuses(overrideCache = false): Observable<Array<ReservationStatus>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.RESERVATION_STATUSES);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), ReservationStatus));
    }

    return this.get<Array<ReservationStatus>>(`${this.resourceUrl}reservationStatuses/`).pipe(
      map(response => {
        this.cacheService.cacheData(
          CacheKeyConstants.RESERVATION_STATUSES,
          JSON.stringify(response),
          CacheExpiryConstants.DOMAIN_CACHE_EXPIRY
        );
        return Deserialize(response, ReservationStatus);
      })
    );
  }

  /**
   * Returns a reservation status by ID
   *
   * @param id
   * @param overrideCache
   */
  findReservationStatusWithId(id: number, overrideCache = false): Observable<ReservationStatus> {
    return this.getReservationStatuses(overrideCache).pipe(
      map(response => response.find(reservationStatus => reservationStatus.id === id))
    );
  }

  /**
   * Returns a reservation status by name
   *
   * @param name
   * @param overrideCache
   */
  findReservationStatusWithName(name: string, overrideCache = false): Observable<ReservationStatus> {
    return this.getReservationStatuses(overrideCache).pipe(
      map(response => response.find(reservationStatus => reservationStatus.name === name))
    );
  }

  /**
   * Returns a reservation route summary by reservation ID
   *
   * @param reservationId
   * @param routePeriodCode
   * @permission reservation_read
   */
  findReservationRouteSummaryByReservationId(reservationId: number, routePeriodCode: string): Observable<ReservationRouteSummary> {
    return this.get<ReservationRouteSummary>(`${this.resourceUrl}${reservationId}/reservationRouteSummary`, {
      routePeriodCode: routePeriodCode
    }).pipe(map(response => Deserialize(response, ReservationRouteSummary)));
  }

  /**
   * Returns a list of cancellation reasons, optionally overriding the cached values
   *
   * @param overrideCache
   */
  listReservationCancellationReasons(): Observable<Array<CancellationReason>> {
    return this.get<Array<CancellationReason>>(`${this.resourceUrl}cancellationReasons/`).pipe(
      map(response => Deserialize(response, CancellationReason))
    );
  }

  /**
   * Gets the driver's estimated time of arrival for a given reservation
   *
   * @param reservationId
   * @permission reservation_read
   */
  getDriverEta(reservationId: number): Observable<DriverEta> {
    return this.get<DriverEta>(`${this.resourceUrl}${reservationId}/estimatedTimeOfArrival`).pipe(
      map(response => Deserialize(response, DriverEta))
    );
  }

  /*****************  Chat Messages *********************/

  /**
   * Returns the chat message list attached to a given reservation
   *
   * @param reservationId
   * @permission chat_message_read
   */
  getChatMessagesForReservation(reservationId: number): Observable<Array<ChatMessage>> {
    return this.get(`reservations/${reservationId}/chatMessages/`).pipe(map(response => Deserialize(response, ChatMessage)));
  }

  /**
   * Sends a chat message from the current logged in user to a provided user by ID, and is
   * attached to a provided reservation by ID. Authentication data will be cleared if it
   * can't be found
   *
   * @param reservationId
   * @param recipientPersonId
   * @param message
   * @permission chat_message_create
   */
  sendChatMessage(reservationId: number, recipientPersonId: number, message: string): Observable<ChatMessage> {
    if (!this.authDataService.isLoggedIn) {
      throw new UnauthorizedException('Unauthorized');
    }

    const chatMessage: ChatMessage = new ChatMessage();

    chatMessage.text = message;
    chatMessage.recipientPersonId = recipientPersonId;
    chatMessage.senderPersonId = this.authDataService.currentUser.claraPersonId;
    chatMessage.reservationId = reservationId;

    return this.post(`${this.resourceUrl}${reservationId}/chatMessages/`, Serialize(chatMessage)).pipe(
      map(response => Deserialize(response, ChatMessage))
    );
  }

  /*****************  Fare Adjustment Review Queue *********************/

  /**
   * @param reservationId
   * @permission reservation_read
   */
  getRiderFaresByReservationId(reservationId: number): Observable<Array<RiderFare>> {
    return this.get<Array<RiderFare>>(`${this.resourceUrl}${reservationId}/riderFares/`).pipe(
      map(response => Deserialize(response, RiderFare))
    );
  }

  /*
   *
   */
  saveRiderFares(reservationId: number, riderFares: Array<RiderFare>): Observable<Array<RiderFare>> {
    return this.post<Array<RiderFare>>(`${this.resourceUrl}${reservationId}/riderFares/?multi=true`, Serialize(riderFares)).pipe(
      map(response => Deserialize(response, RiderFare))
    );
  }

  /**
   * Returns a cancellation reason by ID
   *
   * @param id
   * @param overrideCache
   */
  findCancellationReasonWithId(id: number): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.id === id))
    );
  }

  /**
   * Returns a cancellation reason by name
   *
   * @param name
   * @param overrideCache
   */
  findCancellationReasonWithName(name: string): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.name === name))
    );
  }

  /**
   * Gets the geocoordinate position of the driver attached to a reservaiton
   *
   * @param reservationId
   * @permission reservation_read
   */
  getDriverPosition(reservationId: number): Observable<MiniDriver> {
    return this.get<MiniDriver>(`${this.resourceUrl}${reservationId}/driverPosition`).pipe(
      map(response => Deserialize(response, MiniDriver))
    );
  }

  /**
   * Subscribes to a feed of driver location by reservation ID. Every {interval} seconds, the current
   * location of the driver is returned to the subscriber
   *
   * @param reservationId
   * @param interval the time in seconds between polling requests
   */
  trackDriverLocation(reservationId: number, interval?: number): Observable<MiniDriver> {
    return timer(0, interval).pipe(
      filter(() => this.authDataService.isLoggedIn),
      mergeMap(() => this.getDriverPosition(reservationId))
    );
  }

  /*****************  Insurance Policy *********************/

  /**
   * Returns the insurance policy applicable to a reservation
   *
   * @param reservationId
   * @permission ride_monitoring
   */
  findInsurancePolicyForReservation(reservationId: number): Observable<InsurancePolicy> {
    return this.get(`${this.resourceUrl}${reservationId}/insurancePolicy`).pipe(
      map(response => {
        if (isNullOrUndefined(response)) {
          throw new ServiceException('Insurance Policy Not Found');
        }
        return Deserialize(response, InsurancePolicy);
      })
    );
  }

  /**
   * Gets a timezone adjusted pickup date string with the given format
   *
   * @param date
   * @param timeZone
   * @param dateFormat
   */
  getFormattedTimeZoneDate(date: Date, timeZone: string, dateFormat = 'M/DD/YY h:mm a (tz)'): string {
    return TimeZoneUtil.formatTimeZoneDate(date, timeZone, dateFormat);
  }

  /********* Reservation Status ***************/

  /**
   * Updates a reservation with a new reservation status
   *
   * @param reservationId
   * @param reservationStatusName
   */
  updateReservationStatus(
    reservationId: number,
    reservationStatusName: string,
    timestamp: Date = new Date()
  ): Observable<ReservationDetail> {
    const reservationStatusUpdate: ReservationStatusUpdate = new ReservationStatusUpdate();
    reservationStatusUpdate.reservationId = reservationId;
    reservationStatusUpdate.reservationStatusName = reservationStatusName;
    reservationStatusUpdate.timestamp = timestamp;

    return this.put<RiderProtectedReservationDetail>(
      `${this.resourceUrl}${reservationId}/reservationStatus`,
      Serialize(reservationStatusUpdate)
    ).pipe(map(response => Deserialize(response, ReservationDetail)));
  }

  /***************** Util *********************/

  isCompleted(reservationStatus: ReservationStatus): boolean {
    return reservationStatus.name === ReservationStatusNameConstants.COMPLETED;
  }

  getRiderIdCardImage(reservationId: number): Observable<FileData>{
    return this.get<FileData>(`${this.resourceUrl}${reservationId}/riderIdCardImage`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

}
